/**
 * Configuração do Questionário de Suitability — CVM 30/2021
 *
 * Versione o questionário ao alterar perguntas ou pesos (atualize a versão).
 * Respostas antigas permanecem vinculadas à versão original.
 */

import type {
  InvestorProfileType,
  IncomeRange,
  NetWorthRange,
  InvestmentExperience,
  InvestmentKnowledge,
  InvestmentObjective,
  InvestmentHorizon,
  PortfolioPercentage,
  RiskReaction,
  RiskScenario,
  YesNoPartial,
  PreviousInvestmentType,
  ProfileInfo,
  SuitabilityAnswers,
} from '@/types/suitability';
import type { SimplifiedSuitabilityAnswers, SuitabilityV2Result, ExperienceLevel } from '@/types/onboarding';

// ---------------------------------------------------------------------------
// Versão atual do questionário
// ---------------------------------------------------------------------------
export const CURRENT_SUITABILITY_VERSION = '2026-03-v1';

// ---------------------------------------------------------------------------
// Labels para selects/radios
// ---------------------------------------------------------------------------

export const INCOME_RANGE_LABELS: Record<IncomeRange, string> = {
  ate_3k: 'Até R$ 3.000',
  '3k_10k': 'R$ 3.000 a R$ 10.000',
  '10k_30k': 'R$ 10.000 a R$ 30.000',
  '30k_50k': 'R$ 30.000 a R$ 50.000',
  acima_50k: 'Acima de R$ 50.000',
};

export const NET_WORTH_LABELS: Record<NetWorthRange, string> = {
  ate_100k: 'Até R$ 100.000',
  '100k_500k': 'R$ 100.000 a R$ 500.000',
  '500k_1m': 'R$ 500.000 a R$ 1.000.000',
  '1m_5m': 'R$ 1.000.000 a R$ 5.000.000',
  acima_5m: 'Acima de R$ 5.000.000',
};

export const EXPERIENCE_LABELS: Record<InvestmentExperience, string> = {
  nunca: 'Nunca investi',
  menos_1_ano: 'Menos de 1 ano',
  '1_3_anos': '1 a 3 anos',
  '3_5_anos': '3 a 5 anos',
  mais_5_anos: 'Mais de 5 anos',
};

export const KNOWLEDGE_LABELS: Record<InvestmentKnowledge, string> = {
  nenhuma: 'Nenhuma',
  basica: 'Básica',
  intermediaria: 'Intermediária',
  avancada: 'Avançada',
};

export const OBJECTIVE_LABELS: Record<InvestmentObjective, string> = {
  preservacao: 'Preservação de capital',
  renda_passiva: 'Renda passiva (dividendos)',
  crescimento: 'Crescimento patrimonial',
  especulacao: 'Ganhos de curto prazo',
};

export const HORIZON_LABELS: Record<InvestmentHorizon, string> = {
  menos_1_ano: 'Menos de 1 ano',
  '1_3_anos': '1 a 3 anos',
  '3_5_anos': '3 a 5 anos',
  mais_5_anos: 'Mais de 5 anos',
};

export const PORTFOLIO_PCT_LABELS: Record<PortfolioPercentage, string> = {
  menos_10: 'Menos de 10%',
  '10_30': '10% a 30%',
  '30_50': '30% a 50%',
  mais_50: 'Mais de 50%',
};

export const RISK_REACTION_LABELS: Record<RiskReaction, string> = {
  vender_tudo: 'Venderia tudo imediatamente',
  vender_parte: 'Venderia parte para reduzir exposição',
  manter: 'Manteria e aguardaria recuperação',
  investir_mais: 'Investiria mais aproveitando o preço menor',
};

export const RISK_SCENARIO_LABELS: Record<RiskScenario, string> = {
  seguro_5: 'Ganho máximo de 5% ao ano, sem risco de perda',
  moderado_12: 'Ganho esperado de 12% ao ano, com risco de perda de até 10%',
  agressivo_20: 'Ganho esperado de 20% ao ano, com risco de perda de até 25%',
};

export const YES_NO_PARTIAL_LABELS: Record<YesNoPartial, string> = {
  sim: 'Sim',
  nao: 'Não',
  parcialmente: 'Parcialmente',
};

export const PREVIOUS_INVESTMENT_LABELS: Record<PreviousInvestmentType, string> = {
  poupanca_cdb_tesouro: 'Poupança / CDB / Tesouro Direto',
  acoes_fundos: 'Ações / Fundos de ações',
  fundos_imobiliarios: 'Fundos imobiliários (FIIs)',
  imoveis_diretos: 'Investimentos imobiliários diretos',
  crowdfunding_scp: 'Crowdfunding / SCP',
  criptomoedas: 'Criptomoedas',
  nenhum: 'Nenhum investimento anterior',
};

// ---------------------------------------------------------------------------
// Scoring — pesos por resposta
// ---------------------------------------------------------------------------

export const EXPERIENCE_SCORES: Record<InvestmentExperience, number> = {
  nunca: 1,
  menos_1_ano: 2,
  '1_3_anos': 3,
  '3_5_anos': 4,
  mais_5_anos: 5,
};

export const KNOWLEDGE_SCORES: Record<InvestmentKnowledge, number> = {
  nenhuma: 1,
  basica: 2,
  intermediaria: 4,
  avancada: 5,
};

export const OBJECTIVE_SCORES: Record<InvestmentObjective, number> = {
  preservacao: 1,
  renda_passiva: 2,
  crescimento: 4,
  especulacao: 5,
};

export const HORIZON_SCORES: Record<InvestmentHorizon, number> = {
  menos_1_ano: 1,
  '1_3_anos': 2,
  '3_5_anos': 4,
  mais_5_anos: 5,
};

export const PORTFOLIO_PCT_SCORES: Record<PortfolioPercentage, number> = {
  menos_10: 1,
  '10_30': 2,
  '30_50': 4,
  mais_50: 5,
};

export const RISK_REACTION_SCORES: Record<RiskReaction, number> = {
  vender_tudo: 1,
  vender_parte: 2,
  manter: 4,
  investir_mais: 5,
};

export const RISK_SCENARIO_SCORES: Record<RiskScenario, number> = {
  seguro_5: 1,
  moderado_12: 3,
  agressivo_20: 5,
};

export const EMERGENCY_RESERVE_SCORES: Record<YesNoPartial, number> = {
  nao: 1,
  parcialmente: 3,
  sim: 5,
};

export const INCOME_SCORES: Record<IncomeRange, number> = {
  ate_3k: 1,
  '3k_10k': 2,
  '10k_30k': 3,
  '30k_50k': 4,
  acima_50k: 5,
};

export const NET_WORTH_SCORES: Record<NetWorthRange, number> = {
  ate_100k: 1,
  '100k_500k': 2,
  '500k_1m': 3,
  '1m_5m': 4,
  acima_5m: 5,
};

// Pontuação extra por quantidade de tipos de investimento anteriores
export function previousInvestmentsScore(types: PreviousInvestmentType[]): number {
  if (types.includes('nenhum') || types.length === 0) return 1;
  if (types.length <= 2) return 2;
  if (types.length <= 4) return 4;
  return 5;
}

// ---------------------------------------------------------------------------
// Cálculo do score total e classificação
// ---------------------------------------------------------------------------

/** Score máximo teórico: 55 (11 dimensões × 5) */
export const SCORE_THRESHOLDS = {
  conservador: { min: 0, max: 25 },
  moderado: { min: 26, max: 40 },
  arrojado: { min: 41, max: 55 },
} as const;

export function calculateSuitabilityScore(answers: SuitabilityAnswers): number {
  return (
    INCOME_SCORES[answers.monthlyIncomeRange] +
    NET_WORTH_SCORES[answers.netWorthRange] +
    EXPERIENCE_SCORES[answers.investmentExperience] +
    previousInvestmentsScore(answers.previousInvestments) +
    KNOWLEDGE_SCORES[answers.realEstateKnowledge] +
    OBJECTIVE_SCORES[answers.investmentObjective] +
    HORIZON_SCORES[answers.investmentHorizon] +
    PORTFOLIO_PCT_SCORES[answers.portfolioPercentage] +
    RISK_REACTION_SCORES[answers.riskReaction] +
    RISK_SCENARIO_SCORES[answers.riskScenario] +
    EMERGENCY_RESERVE_SCORES[answers.hasEmergencyReserve]
  );
}

export function classifyProfile(score: number): InvestorProfileType {
  if (score <= SCORE_THRESHOLDS.conservador.max) return 'conservador';
  if (score <= SCORE_THRESHOLDS.moderado.max) return 'moderado';
  return 'arrojado';
}

// ---------------------------------------------------------------------------
// Metadados dos perfis (exibição)
// ---------------------------------------------------------------------------

export const PROFILE_INFO: Record<InvestorProfileType, ProfileInfo> = {
  conservador: {
    type: 'conservador',
    label: 'Conservador',
    description:
      'Você prioriza a preservação do capital investido. Prefere investimentos com menor risco, mesmo que isso signifique retornos mais modestos. Indicado para investimentos de baixo risco com retorno previsível.',
    color: 'text-blue-600 bg-blue-50 border-blue-200',
    icon: 'Shield',
  },
  moderado: {
    type: 'moderado',
    label: 'Moderado',
    description:
      'Você busca um equilíbrio entre segurança e rentabilidade. Aceita algum nível de risco para obter retornos melhores, mas sem abrir mão de certa estabilidade. Adequado para a maioria dos investimentos imobiliários.',
    color: 'text-amber-600 bg-amber-50 border-amber-200',
    icon: 'Scale',
  },
  arrojado: {
    type: 'arrojado',
    label: 'Arrojado',
    description:
      'Você tem alta tolerância a risco e busca maximizar retornos. Está disposto a aceitar volatilidade e possíveis perdas em troca de ganhos maiores. Habilitado para todos os tipos de investimento da plataforma.',
    color: 'text-red-600 bg-red-50 border-red-200',
    icon: 'Rocket',
  },
};

// ---------------------------------------------------------------------------
// Compatibilidade Produto × Perfil
// ---------------------------------------------------------------------------

export type RiskLevel = 'baixo' | 'medio' | 'alto';

export interface CompatibilityResult {
  allowed: boolean;
  warning: boolean;
  message: string;
}

const COMPATIBILITY_MATRIX: Record<RiskLevel, Record<InvestorProfileType, CompatibilityResult>> = {
  baixo: {
    conservador: { allowed: true, warning: false, message: '' },
    moderado: { allowed: true, warning: false, message: '' },
    arrojado: { allowed: true, warning: false, message: '' },
  },
  medio: {
    conservador: {
      allowed: true,
      warning: true,
      message:
        'Este investimento possui risco médio e pode não ser adequado ao seu perfil conservador. Deseja prosseguir mesmo assim?',
    },
    moderado: { allowed: true, warning: false, message: '' },
    arrojado: { allowed: true, warning: false, message: '' },
  },
  alto: {
    conservador: {
      allowed: false,
      warning: true,
      message:
        'Este investimento possui alto risco e não é adequado ao seu perfil conservador. Para investir, atualize seu perfil de investidor.',
    },
    moderado: {
      allowed: true,
      warning: true,
      message:
        'Este investimento possui alto risco. Seu perfil moderado permite o investimento, mas recomendamos atenção especial aos riscos envolvidos.',
    },
    arrojado: { allowed: true, warning: false, message: '' },
  },
};

export function checkProductCompatibility(
  investorProfile: InvestorProfileType,
  productRiskLevel: RiskLevel,
): CompatibilityResult {
  return COMPATIBILITY_MATRIX[productRiskLevel][investorProfile];
}

// ---------------------------------------------------------------------------
// Steps do formulário
// ---------------------------------------------------------------------------

export const SUITABILITY_STEPS = [
  { id: 1, title: 'Dados Pessoais', description: 'Informações complementares' },
  { id: 2, title: 'Experiência', description: 'Histórico de investimentos' },
  { id: 3, title: 'Objetivos', description: 'Metas e horizonte' },
  { id: 4, title: 'Tolerância a Risco', description: 'Cenários e reações' },
  { id: 5, title: 'Situação Financeira', description: 'Capacidade de investimento' },
] as const;

export const TOTAL_STEPS = SUITABILITY_STEPS.length;

// ---------------------------------------------------------------------------
// V2 Suitability Constants (Simplified 4-question flow)
// ---------------------------------------------------------------------------

export const CURRENT_SUITABILITY_VERSION_V2 = '2026-03-v2';

export const EXPERIENCE_LEVEL_CARDS: Array<{
  value: ExperienceLevel;
  label: string;
  icon: string;
}> = [
  { value: 'iniciante', label: 'Iniciante, dando os primeiros passos e buscando aprender mais', icon: '\u{1F642}' },
  { value: 'intermediario', label: 'Intermedi\u00e1rio, ampliando conhecimentos e explorando novas oportunidades', icon: '\u{1F9D0}' },
  { value: 'experiente', label: 'Experiente, assumindo riscos e buscando aumentar meus retornos', icon: '\u{1F60E}' },
  { value: 'qualificado', label: 'Qualificado, possuo mais de R$ 1 milh\u00e3o investidos ou me autodeclaro como tal', icon: '\u{1F929}' },
];

export const CVM_CERTIFICATIONS = [
  'CEA', 'CGA', 'CFP', 'CFA', 'AAI', 'CNPI',
  'ACIIA (Foundation Level)', 'CFA (Level II)', 'FINRA (Series 86)',
  'Ancord', 'PQO', 'CPA10', 'CPA20', 'CAIA', 'FRM', 'CQF',
  'CFG', 'CGE', 'ABECIP (CA 600, CA 400 e CA 300)', 'CTP', 'FPA',
] as const;

// ---------------------------------------------------------------------------
// V2 Scoring and Classification
// ---------------------------------------------------------------------------

export function calculateV2Score(answers: SimplifiedSuitabilityAnswers): SuitabilityV2Result {
  // Qualified investor: certification or >= R$1M portfolio
  if (answers.hasCertification || answers.portfolioValue >= 1_000_000) {
    return { profileType: 'arrojado', score: 100, investmentLimit: -1 }; // -1 = unlimited
  }
  // Experienced with high income
  if (answers.experienceLevel === 'experiente' && answers.annualGrossIncome > 100_000) {
    const limit = Math.max(answers.annualGrossIncome * 0.1, answers.portfolioValue * 0.1);
    return { profileType: 'arrojado', score: 80, investmentLimit: limit };
  }
  // Intermediate or decent income
  if (
    answers.experienceLevel === 'intermediario' ||
    answers.experienceLevel === 'experiente' ||
    answers.annualGrossIncome > 50_000
  ) {
    const limit = Math.max(answers.annualGrossIncome * 0.1, answers.portfolioValue * 0.1);
    return { profileType: 'moderado', score: 50, investmentLimit: Math.max(limit, 20_000) };
  }
  // Conservative (beginner, low income)
  const limit = Math.max(answers.annualGrossIncome * 0.1, answers.portfolioValue * 0.1);
  return { profileType: 'conservador', score: 20, investmentLimit: Math.max(limit, 20_000) };
}

export function calculateInvestmentLimit(
  annualIncome: number,
  portfolioValue: number,
  isQualified: boolean,
): number {
  if (isQualified) return -1; // unlimited
  const calculated = Math.max(annualIncome * 0.1, portfolioValue * 0.1);
  return Math.max(calculated, 20_000); // minimum R$20k fallback
}
